import { useFileRecord } from "@/api/fileRecords";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { FileRecordDetailPaneView } from "@/panes/file-record-detail/FileRecordDetailPaneView";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import { AlertCircle, File, RefreshCw } from "lucide-react";
import { useCallback } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

/**
 * Main-route handler for `/file-record/:id`. This route is only reached by a
 * direct/shared link or, on mobile, by in-app opens (which can't use a side
 * pane). Everywhere else opens the file in the right pane via `useOpenFileRecord`.
 *
 * On desktop we redirect a direct link **into the side pane** (over Home) so it
 * matches the normal in-app experience. On mobile we render the file full-page
 * since there's no room for a side pane.
 */
export function FileRecordPage() {
  const { fileRecordId } = useParams<{ fileRecordId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const md = useBreakpoint("md");

  // Accept a pre-resolved record from route state (legacy entity adapter path).
  const stateRecord: FileRecord | undefined = location.state?.fileRecord;

  // Only the mobile full-page render needs the record; on desktop we redirect
  // to the pane (passing `undefined` disables the query). Hooks run
  // unconditionally to respect the rules of hooks — the branch is in render.
  const {
    data: fetchedRecord,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useFileRecord(md ? undefined : fileRecordId, {
    placeholderData: stateRecord,
    refetchOnMount: "always",
  });

  const record = fetchedRecord ?? stateRecord;

  const handleClose = useCallback(() => {
    // A fresh deep link has no history to go back to — send the user Home.
    if (location.key === "default") {
      navigate(ROUTES.HOME);
      return;
    }
    navigate(-1);
  }, [navigate, location.key]);

  // Desktop: open the file in the right side pane over Home.
  if (md) {
    if (!fileRecordId) return <Navigate to={ROUTES.HOME} replace />;
    return (
      <Navigate
        to={ROUTES.HOME}
        replace
        state={{
          rightPane: {
            pathname: route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId }),
            search: "",
            hash: "",
            key: `pane-${Date.now()}-${Math.random()}`,
          },
          // Land expanded (full-width minus header + left rail) for a focused
          // read of the shared doc; the layout applies this once.
          rightPaneExpanded: true,
        }}
      />
    );
  }

  // Mobile full-page fallback.
  if (record) {
    return (
      <div className="h-full w-full flex flex-col">
        <FileRecordDetailPaneView
          record={record}
          showPaneClose
          onClose={handleClose}
        />
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-4 p-8">
      {!fileRecordId && (
        <>
          <File className="h-12 w-12 text-muted-foreground" />
          <div className="text-muted-foreground text-center">
            <p className="font-medium">No file selected</p>
            <p className="text-sm">No file record ID in the URL.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleClose}>
            Go back
          </Button>
        </>
      )}

      {fileRecordId && isLoading && !record && (
        <>
          <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-muted-foreground">Loading file...</p>
        </>
      )}

      {fileRecordId && error && !isLoading && (
        <>
          <div className="p-4 rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground">Failed to load file</p>
            <p className="text-sm text-muted-foreground mt-1">
              The file couldn&apos;t be retrieved. It may have been deleted or
              your session expired.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
              className="gap-2"
            >
              <RefreshCw
                className={cn("h-4 w-4", isRefetching && "animate-spin")}
              />
              Retry
            </Button>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Go back
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
